import {Component, OnInit, inject, output, input, effect} from '@angular/core';
import {ProjectStatus, ViewMode, Right} from "../../../interfaces";
import {UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {DataService} from "../../../../services/data.service";
import {FormService} from "../../../../services/form.service";
import {ProjectStatusPipe} from "../../../../pipes/project-status.pipe";
import {AccessService} from "../../../../services/access.service";
import Swal from "sweetalert2";
import {ToastrService} from "ngx-toastr";
import {NgIf, NgFor} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {InfluencePipe} from '../../../../pipes/influence.pipe';
import {InterestPipe} from '../../../../pipes/interest.pipe';

@Component({
    selector: 'app-project-stakeholder',
    templateUrl: './project-stakeholder.component.html',
    styleUrl: './project-stakeholder.component.scss',
    imports: [NgIf, TooltipModule, FormsModule, ReactiveFormsModule, NgFor, InfluencePipe, InterestPipe]
})
export class ProjectStakeholderComponent implements OnInit {

    accessService = inject(AccessService);
    formService = inject(FormService);
    private dataService = inject(DataService);
    private fb = inject(UntypedFormBuilder);
    private projectStatusPipe = inject(ProjectStatusPipe);
    private toastrService = inject(ToastrService);

    readonly PID = input.required<any>();
    readonly reloadTrigger = input<number>(0);

    readonly changeProject = output();
    readonly changeViewMode = output<boolean>();

    readonly ViewMode = ViewMode;
    readonly Right = Right;

    viewMode!: ViewMode;
    project: any;
    form!: UntypedFormGroup;

    private reloadEffect = effect(() => {
        if (this.reloadTrigger() > 0) {
            this.getProject();
        }
    })

    ngOnInit(): void {
        this.viewMode = ViewMode.VIEW;

        this.form = this.fb.group({
            stakeholder: this.fb.array([]),
        });

        if (this.PID() !== '0') {
            this.getProject();
        }
    }

    getProject(): void {
        this.dataService.request('Project/get', {
            PID: this.PID()
        }).subscribe(response => {
            this.project = response.project;

            this.patchForm();
        });
    }

    patchForm(): void {
        this.formService.getFormArray(this.form, 'stakeholder').clear();
        this.project.stakeholder.forEach(item => {
            this.addFormItem(item, 'stakeholder');
        });
    }

    addFormItem(data: any = {}, control): void {
        const newGroup = this.fb.group({
            id: [data.id || null],
            id_project: [data.id_project || this.project.id],
            name: [data.name || null, Validators.required],
            influence: [data.influence || '', Validators.required],
            interest: [data.interest || '', Validators.required],
            expectations: [data.expectations || null, Validators.required],
            measures: [data.measures || null, Validators.required],
            _deleted: [false],
        });

        this.formService.getFormArray(this.form, control).push(newGroup);
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
        if (this.viewMode == ViewMode.EDIT) {
            this.changeViewMode.emit(true);
        } else {
            this.changeViewMode.emit(false);
        }
    }

    update(): void {
        if (![ProjectStatus.REVISE].includes(this.project.status)) {
            this.dataService.request('Project/updateStakeholder', {
                project: this.form.getRawValue(),
            }).subscribe(() => {
                this.toastrService.success('Erfolgreich gespeichert')
                this.setMode(ViewMode.VIEW);
                this.getProject();
                this.changeProject.emit();
            });
        } else {
            Swal.fire({
                title: 'Sicher?',
                text: 'Der Status wird auf "' + this.projectStatusPipe.transform(ProjectStatus.IN_ELABORATION) + '" gesetzt.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText: 'Ja',
                cancelButtonText: 'Nein'
            }).then(result => {
                if (result.value) {
                    this.dataService.request('Project/setStatus', {
                        PID: this.PID(),
                        status: ProjectStatus.IN_ELABORATION,
                    }).subscribe(() => {
                        this.dataService.request('Project/updateStakeholder', {
                            project: this.form.getRawValue(),
                        }).subscribe(() => {
                            this.toastrService.success('Erfolgreich gespeichert')
                            this.setMode(ViewMode.VIEW);
                            this.getProject();
                            this.changeProject.emit();
                        });
                    });
                }
            });
        }
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    markEntryAsDeleted(entry): void {
        entry.patchValue({
            _deleted: true,
        });
        for (const key in entry.controls) {
            entry.get(key).clearValidators();
            entry.get(key).updateValueAndValidity();
        }
    }

    getLengthOfFormArray(name): number {
        return this.formService.getFormArray(this.form, name).controls.filter(control => {
            return control.value._deleted == false;
        }).length;
    }
}
